import { useCallback, useEffect, useState } from 'react';

export interface ColumnConfig {
  field: string;
  header: string;
  width?: string;
}

export interface UseColumnSettingsOptions {
  orderStorageKey: string;
  widthsStorageKey: string;
  defaultOrder: string[];
  defaultWidths: Record<string, string>;
  headerToFieldMap: Map<string, string>;
  tableRef: React.RefObject<HTMLDivElement>;
  visibleColumns?: ColumnConfig[];
  allFields: string[];
}

export interface UseColumnSettingsReturn {
  columnOrder: string[];
  columnWidths: Record<string, string>;
  handleColumnResizeEnd: (e: any) => void;
  updateColumnSettings: () => void;
}

function mergeColumnOrder(
  savedOrder: string[],
  defaultOrder: string[],
  allFields: string[]
): string[] {
  const normalize = (arr: string[]) =>
    Array.from(new Set(arr))
      .filter((x) => typeof x === 'string' && x.trim().length > 0)
      .filter((x) => x !== 'column')
      .filter((x) => allFields.includes(x));

  const result = normalize(savedOrder);
  const has = new Set(result);

  const missing = defaultOrder
    .filter((f) => allFields.includes(f))
    .filter((f) => !has.has(f));

  const findPrev = (field: string) => {
    const i = defaultOrder.indexOf(field);
    for (let p = i - 1; p >= 0; p--) {
      const cand = defaultOrder[p];
      if (has.has(cand)) return cand;
    }
    return undefined;
  };

  const findNext = (field: string) => {
    const i = defaultOrder.indexOf(field);
    for (let n = i + 1; n < defaultOrder.length; n++) {
      const cand = defaultOrder[n];
      if (has.has(cand)) return cand;
    }
    return undefined;
  };

  for (const field of missing) {
    const prev = findPrev(field);
    const next = findNext(field);

    if (prev) {
      const prevPos = result.indexOf(prev);
      let insertPos = prevPos + 1;

      if (next) {
        const nextPos = result.indexOf(next);
        insertPos = Math.min(insertPos, nextPos);
      }

      result.splice(insertPos, 0, field);
    } else if (next) {
      result.splice(result.indexOf(next), 0, field);
    } else {
      result.push(field);
    }

    has.add(field);
  }

  return result;
}

export const useColumnSettings = (
  options: UseColumnSettingsOptions
): UseColumnSettingsReturn => {
  const {
    orderStorageKey,
    widthsStorageKey,
    defaultOrder,
    defaultWidths,
    headerToFieldMap,
    tableRef,
    visibleColumns,
    allFields
  } = options;

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(orderStorageKey);
    if (!saved) return defaultOrder;

    try {
      const parsed = JSON.parse(saved);
      if (!Array.isArray(parsed) || parsed.length === 0) {
        localStorage.removeItem(orderStorageKey);
        return defaultOrder;
      }

      const merged = mergeColumnOrder(parsed, defaultOrder, allFields);

      if (JSON.stringify(merged) !== JSON.stringify(parsed)) {
        localStorage.setItem(orderStorageKey, JSON.stringify(merged));
      }

      return merged;
    } catch {
      localStorage.removeItem(orderStorageKey);
      return defaultOrder;
    }
  });

  const [columnWidths, setColumnWidths] = useState<Record<string, string>>(
    () => {
      const saved = localStorage.getItem(widthsStorageKey);
      if (saved) {
        return JSON.parse(saved);
      }
      return defaultWidths;
    }
  );

  useEffect(() => {
    localStorage.setItem(orderStorageKey, JSON.stringify(columnOrder));
  }, [columnOrder, orderStorageKey]);

  useEffect(() => {
    localStorage.setItem(widthsStorageKey, JSON.stringify(columnWidths));
  }, [columnWidths, widthsStorageKey]);

  const handleColumnResizeEnd = useCallback(
    (e: any) => {
      const { column, element } = e;
      const field =
        column?.props?.columnKey ||
        column?.props?.field ||
        column?.columnKey ||
        column?.field;

      if (field && element && allFields.includes(field)) {
        const width = `${element.offsetWidth}px`;
        setColumnWidths((prev) => ({
          ...prev,
          [field]: width
        }));
      }
    },
    [allFields]
  );

  const updateColumnSettings = useCallback(() => {
    const container = tableRef.current;
    if (!container) return;

    const thead = container.querySelector('.p-datatable-thead tr');
    if (!thead) return;

    const thElements = Array.from(thead.querySelectorAll('th'));
    const newOrder: string[] = [];
    const newWidths: Record<string, string> = {};

    let sortedColumns: string[] = [];
    if (visibleColumns) {
      // Сортируем visibleColumns по текущему порядку
      sortedColumns = [...visibleColumns]
        .sort((a, b) => {
          const aIndex = columnOrder.indexOf(a.field);
          const bIndex = columnOrder.indexOf(b.field);
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        })
        .map((col) => col.field);
    } else {
      sortedColumns = [...defaultOrder].sort((a, b) => {
        const aIndex = columnOrder.indexOf(a);
        const bIndex = columnOrder.indexOf(b);
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    }

    thElements.forEach((th, index) => {
      let field: string | null = null;

      const headerText = th.textContent?.trim() || '';
      field = headerToFieldMap.get(headerText) || null;

      if (!field) {
        const columnKeyAttr = th.getAttribute('data-pc-name');
        if (columnKeyAttr && columnKeyAttr.startsWith('columnheader-')) {
          const potentialField = columnKeyAttr.replace('columnheader-', '');
          if (allFields.includes(potentialField)) {
            field = potentialField;
          }
        }
      }

      if (!field && sortedColumns[index]) {
        field = sortedColumns[index];
      }

      if (field && allFields.includes(field)) {
        if (
          !visibleColumns ||
          visibleColumns.some((col) => col.field === field)
        ) {
          newOrder.push(field);

          const computedStyle = window.getComputedStyle(th);
          const width = computedStyle.width;

          if (width && width !== 'auto' && width !== '0px') {
            newWidths[field] = width;
          }
        }
      }
    });

    if (newOrder.length > 0) {
      if (visibleColumns) {
        if (newOrder.length === visibleColumns.length) {
          const currentOrderStr = JSON.stringify(columnOrder);
          const newOrderStr = JSON.stringify(newOrder);
          if (newOrderStr !== currentOrderStr) {
            setColumnOrder(newOrder);
          }
        }
      } else {
        const currentOrderStr = JSON.stringify(columnOrder);
        const newOrderStr = JSON.stringify(newOrder);
        if (newOrderStr !== currentOrderStr) {
          setColumnOrder(newOrder);
        }
      }
    }

    if (Object.keys(newWidths).length > 0) {
      const hasChanges = Object.keys(newWidths).some(
        (field) => columnWidths[field] !== newWidths[field]
      );
      if (hasChanges) {
        setColumnWidths((prev) => ({
          ...prev,
          ...newWidths
        }));
      }
    }
  }, [
    columnOrder,
    columnWidths,
    headerToFieldMap,
    tableRef,
    visibleColumns,
    defaultOrder,
    allFields
  ]);

  return {
    columnOrder,
    columnWidths,
    handleColumnResizeEnd,
    updateColumnSettings
  };
};
